import { FiHeart, FiShoppingCart, FiStar } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useToast } from '../../context/ToastContext';
import { useWishlist } from '../../context/WishlistContext';
import { getBookImage, handleBookImageError } from '../../utils';
import StockOverlay from '../../components/common/StockOverlay';

const SearchResultSkeleton = () => (
  <div className="grid animate-pulse grid-cols-[96px_minmax(0,1fr)] gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:grid-cols-[176px_minmax(0,1fr)] sm:gap-6 sm:rounded-none sm:p-5">
    <div className="h-36 rounded-lg bg-gray-200 sm:row-span-2 sm:h-60" />
    <div className="min-w-0 space-y-3 py-1">
      <div className="h-5 w-3/4 rounded bg-gray-200" />
      <div className="h-3 w-1/3 rounded bg-gray-200" />
      <div className="h-4 w-1/4 rounded bg-gray-200" />
      <div className="h-7 w-2/3 rounded bg-gray-200" />
    </div>
    <div className="col-span-2 h-11 rounded-lg bg-gray-200 sm:col-span-1 sm:col-start-2 sm:w-36" />
  </div>
);

const SearchResultCard = ({ book }) => {
  const { addToCart } = useCart();
  const { formatBookPrice, getBookAmount, getBookDiscount } = useCurrency();
  const { addToast } = useToast();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const wished = isInWishlist(book.id);
  const rating = Number(book.rating) || 0;
  const reviewCount = book.reviewCount || book.reviews?.length || 0;
  const discount = getBookDiscount(book);
  const inStock = Number(book.stock) > 0;

  const addBook = () => {
    addToCart(book);
    addToast(`"${book.title}" added to cart`, 'success');
  };

  const toggleWishlist = () => {
    if (wished) removeFromWishlist(book.id);
    else addToWishlist(book);
    addToast(wished ? 'Removed from wishlist' : 'Added to wishlist', wished ? 'info' : 'success');
  };

  return (
    <article className="relative grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition hover:border-orange-200 sm:grid-cols-[176px_minmax(0,1fr)] sm:gap-x-6 sm:rounded-none sm:border-0 sm:border-b sm:p-5 sm:shadow-none sm:last:border-b-0">
      <Link to={`/book/${book.id}`} className="relative block h-36 overflow-hidden rounded-lg bg-gray-50 sm:row-span-2 sm:h-60">
        <img
          src={getBookImage(book.image)}
          onError={handleBookImageError}
          alt={book.title}
          loading="lazy"
          className="h-full w-full object-contain p-1 transition duration-300 hover:scale-105 sm:p-2"
        />
        <StockOverlay stock={book.stock} />
      </Link>
      <div className="min-w-0 sm:pr-10">
        {book.isBestseller && (
          <span className="mb-1.5 inline-flex rounded-md bg-orange-50 px-2 py-1 text-[10px] font-semibold text-orange-700 ring-1 ring-inset ring-orange-100 sm:mb-2 sm:text-[11px]">
            Bestseller
          </span>
        )}
        <h2 className="break-words text-sm font-semibold leading-5 text-gray-900 sm:text-xl sm:leading-snug">
          <Link to={`/book/${book.id}`} className="line-clamp-3 hover:text-orange-600 sm:line-clamp-none">{book.title}</Link>
        </h2>
        <p className="mt-1 break-words text-xs leading-5 text-gray-500 sm:text-sm">
          {book.author ? `by ${book.author}` : book.publisher || book.category?.name}
          {book.publisherDate && ` · ${new Date(book.publisherDate).toLocaleDateString()}`}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs sm:text-sm" aria-label={`${rating} out of 5 stars`}>
          <span className="font-medium text-black">{rating.toFixed(1)}</span>
          <span className="flex shrink-0 gap-0.5" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((index) => (
              <FiStar key={index} className={index < Math.round(rating) ? 'fill-orange-500 text-orange-500' : 'text-gray-300'} />
            ))}
          </span>
          <span className="text-xs text-gray-500 sm:text-sm">({reviewCount})</span>
        </div>
        {(book.binding || book.language) && (
          <p className="mt-1.5 break-words text-xs leading-5 text-gray-500 sm:mt-2 sm:text-sm">
            {[book.binding, book.language].filter(Boolean).join(' · ')}
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">{formatBookPrice(book)}</span>
          {getBookAmount(book, 'originalPrice') > getBookAmount(book) && (
            <span className="text-xs text-gray-500 sm:text-sm">M.R.P: <span className="line-through">{formatBookPrice(book, 'originalPrice')}</span></span>
          )}
          {discount > 0 && <span className="text-xs font-semibold text-green-700 sm:text-sm">({discount}% off)</span>}
        </div>
        {book.description && <p className="mt-2 hidden max-w-3xl text-sm text-gray-600 line-clamp-2 md:block">{book.description}</p>}
      </div>
      <div className="col-span-2 flex min-w-0 items-center gap-3 border-t border-gray-100 pt-3 sm:col-span-1 sm:col-start-2 sm:block sm:border-0 sm:pt-0">
        <p className={`flex shrink-0 items-center gap-1.5 text-base font-medium ${inStock ? 'text-green-700' : 'text-red-600'}`}>
          <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${inStock ? 'bg-green-600' : 'bg-red-500'}`} />
          {inStock ? `${book.stock} in stock` : 'Coming soon'}
        </p>
        <button
          type="button"
          onClick={addBook}
          disabled={!inStock}
          className="inline-flex min-h-11 min-w-0 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-orange-600 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 sm:mt-3 sm:px-6"
        >
          <FiShoppingCart className="shrink-0 text-base" aria-hidden="true" />
          <span>{inStock ? 'Add to cart' : 'Coming soon'}</span>
        </button>
      </div>
      <button
        type="button"
        onClick={toggleWishlist}
        aria-pressed={wished}
        aria-label={wished ? 'Remove from wishlist' : 'Add to wishlist'}
        className="absolute left-2 top-2 flex h-11 w-11 items-center justify-center rounded-full border border-gray-100 bg-white/95 shadow-sm transition hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 sm:left-auto sm:right-4 sm:top-4"
      >
        <FiHeart className={wished ? 'fill-red-500 text-red-500' : 'text-gray-700'} />
      </button>
    </article>
  );
};

const SearchResultsList = ({ loading, books, onClearFilters }) => {
  if (loading) return <div className="space-y-3 sm:space-y-0">{Array.from({ length: 5 }).map((_, index) => <SearchResultSkeleton key={index} />)}</div>;

  if (!books.length) {
    return (
      <section className="bg-white px-6 py-20 text-center">
        <h2 className="text-xl font-semibold text-black">No matching products found</h2>
        <p className="mt-2 text-sm text-gray-600">Check the spelling or try fewer words.</p>
        <button type="button" onClick={onClearFilters} className="mt-5 rounded-full bg-orange-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-orange-700">
          Clear search and filters
        </button>
      </section>
    );
  }

  return (
    <section aria-label="Search results">
      <div className="space-y-3 sm:space-y-0 sm:overflow-hidden sm:rounded-xl sm:border sm:border-gray-200">
        {books.map((book) => <SearchResultCard key={book.id} book={book} />)}
      </div>
    </section>
  );
};

export default SearchResultsList;
